import { defineCrestModule } from '@crest/platform-contracts/modules';
import { platformOptionalActionProviderHostCapability } from '@crest/platform-contracts/hosts';
import { workflowsRulePreviewCapability, workflowsRulesCapability, } from './contracts/workflows-rule.contract.js';
import { workflowsTaskActionContractVersion, } from './contracts/workflows-task-action-consumer.contract.js';
export const workflowsModuleManifest = defineCrestModule({
    id: 'workflows',
    version: '1.0.0',
    kind: 'business',
    required: false,
    requires: ['platform'],
    requiredCapabilities: [
        'platform.tenant-context.v1',
        'platform.module-access.v1',
        'platform.owner-invocation-receipts.v1',
        platformOptionalActionProviderHostCapability,
    ],
    optionalCapabilities: [workflowsTaskActionContractVersion],
    optionalCapabilityFallbacks: [{
            capability: workflowsTaskActionContractVersion,
            degradedMode: 'workflows.degraded.task-actions-unavailable.v1',
        }],
    provides: [workflowsRulesCapability, workflowsRulePreviewCapability, 'workflows.executions.v1'],
    permissions: [
        'workflows.rules.read',
        'workflows.rules.manage',
        'workflows.rules.publish',
    ],
    settings: [
        'workflows.rules.configuration',
        'workflows.rules.display',
        'workflows.copy.rules',
    ],
    copyCatalogs: ['workflows.copy.rules'],
    events: {
        publishes: [
            'workflows.rule-created.v1',
            'workflows.rule-version-created.v1',
            'workflows.rule-published.v1',
            'workflows.rule-enabled.v1',
            'workflows.rule-disabled.v1',
            'workflows.rule-archived.v1',
            'workflows.rule-rolled-back.v1',
        ],
        subscribes: ['projects.project-updated.v2'],
    },
    server: {
        routes: [{ id: 'workflows.routes.rules', priority: 0 }],
        jobs: [{ id: 'workflows.jobs.action-intent-execution', priority: 20 }],
        subscribers: [{
                id: 'workflows.subscribers.project-updated.v2',
                requiredCapability: 'platform.tenant-context.v1',
                priority: 0,
            }],
        slots: [],
    },
    web: {
        routes: [{ id: 'workflows.web.rules', priority: 0 }],
        navigation: [{ id: 'workflows.navigation.rules', priority: 70 }],
        slots: [],
    },
    mobile: { routes: [], slots: [] },
    portal: { routes: [], slots: [] },
    database: {
        schema: 'workflows',
        owns: [
            'workflows.rules',
            'workflows.rule_versions',
            'workflows.rule_activations',
            'workflows.rule_commands',
            'workflows.execute_rule_command',
            'workflows.event_receipts',
            'workflows.execution_runs',
            'workflows.action_attempts',
            'workflows.action_intent_executions',
            'workflows.select_active_rule_versions_v1',
            'workflows.consume_project_updated_v1',
            'workflows.claim_action_intent_executions_v1',
            'workflows.mark_action_intent_invocation_v1',
            'workflows.complete_action_intent_execution_v1',
            'workflows.revalidate_action_intent_execution_v1',
            'workflows.issue_action_intent_owner_invocation_v1',
        ],
        readsThrough: ['platform.owner-invocation-receipts.v1'],
        migrations: [
            'workflows.rule-management',
            'workflows.project-update-runs',
            'workflows.action-intent-execution',
            'workflows.execution-authority-correction',
            'workflows.execution-lifecycle-fence',
            'workflows.execution-serialization-root',
            'workflows.execution-account-family-and-visibility',
            'workflows.owner-invocation-receipts',
        ],
    },
    health: ['workflows.database'],
    lifecycle: {
        initialization: { timeoutMs: 5_000, failureBehavior: 'quarantine-module' },
        resources: {
            requestConcurrency: 64,
            // The Project-update subscriber executes under the worker budget: zero
            // capacity here silently starved every delivery (found by the CR-011
            // real-transport proof).
            jobConcurrency: 1,
            queueBacklogLimit: 500,
            databaseConnectionBudget: 8,
            workerIsolation: 'shared-api',
        },
        disable: {
            data: 'retain',
            clientContributions: 'remove',
            serverRoutes: 'deny-tenant-access',
            scheduledJobs: 'skip',
            events: {
                behavior: 'drop-safe',
                durableCursor: false,
                retentionDays: 0,
                backlogLimit: 0,
                warningAt: 0,
                reconciliationProcedure: null,
                dropSafeReason: 'Rule evaluation is deliberately not retroactive: Project-update events arriving while Workflows is disabled are never replayed into rule runs after re-enable, and pending action intents are created only from live events.',
            },
            outboundSideEffects: 'suppress-no-retroactive-send',
            inFlight: 'finish-idempotently',
        },
        resume: {
            configuration: 'last-compatible-published',
            healthChecks: ['workflows.database'],
            reconciliationProcedure: null,
            visibility: 'after-health-and-reconciliation',
        },
        uninstall: {
            executableCode: 'remove',
            data: 'retain',
            tombstone: 'retain-immutable',
            destruction: 'separate-explicit-authority',
        },
    },
    retentionPolicy: 'Workflow rules, immutable versions, lifecycle history, and replay-safe commands remain stored while disabled.',
});
//# sourceMappingURL=workflows.module.js.map